/* eslint-disable react-hooks/set-state-in-effect */
/**
 * ChannelRulesBuilder – Variante-A-Layout
 *
 * Links: Channel-Liste (reused ChannelRulesList)
 * Rechts: Text-Regeln Panel (reused ChannelTextRulesPanel)
 *
 * Komposition analog zu ChannelRulesTab (workspace-settings) —
 * liest Channels + alle Regeln für Rule-Counts, delegiert CRUD an Reuse-Komponenten.
 */

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useChannels, useAllChannelRequirements } from '@/hooks/useChannels'
import { useAllTextRules } from '@/hooks/useTextRules'
import { ChannelRulesList } from '@/components/v2/workspace-settings/channel-rules/ChannelRulesList'
import { ChannelTextRulesPanel } from '@/components/v2/workspace-settings/channel-rules/ChannelTextRulesPanel'

interface ChannelRulesBuilderProps {
  initialChannelId?: string
}

export function ChannelRulesBuilder({ initialChannelId }: ChannelRulesBuilderProps = {}) {
  const { t } = useTranslation()
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(initialChannelId ?? null)

  const { data: rawChannels = [], isLoading } = useChannels()
  const { data: allTextRules = [] } = useAllTextRules()
  const { data: allRequirements = [] } = useAllChannelRequirements()

  const textRuleCounts = new Map<string, number>()
  for (const rule of allTextRules) {
    const cid = rule.channel_id
    textRuleCounts.set(cid, (textRuleCounts.get(cid) || 0) + 1)
  }

  const imageRuleCounts = new Map<string, number>()
  for (const req of allRequirements) {
    const cid = req.channel_id
    let count = 0
    if (req.min_width) count++
    if (req.min_height) count++
    if (req.max_width) count++
    if (req.max_height) count++
    if (req.allowed_formats?.length) count++
    imageRuleCounts.set(cid, count)
  }

  const channels = rawChannels.map((ch) => ({
    id: ch.id,
    channel_name: ch.channel_name,
    channel_type: ch.channel_type,
    integration_name: ch.integration_name,
    imageRuleCount: imageRuleCounts.get(ch.id) || 0,
    textRuleCount: textRuleCounts.get(ch.id) || 0,
  }))

  useEffect(() => {
    if (!selectedChannelId && channels.length > 0) {
      setSelectedChannelId(channels[0].id)
    }
  }, [channels, selectedChannelId])

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 h-full">
      <div className="border border-neutral-200 dark:border-neutral-700 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-3">
          {t('channelRules.title')}
        </h3>
        <ChannelRulesList
          channels={channels}
          selectedId={selectedChannelId}
          onSelect={setSelectedChannelId}
          isLoading={isLoading}
        />
      </div>

      <div className="border border-neutral-200 dark:border-neutral-700 rounded-xl p-5">
        {selectedChannelId ? (
          <div
            data-testid="channel-rules-panel"
            data-channel={selectedChannelId}
          >
            <ChannelTextRulesPanel
              key={`txt-${selectedChannelId}`}
              channelId={selectedChannelId}
            />
          </div>
        ) : (
          <div
            data-testid="channel-rules-prompt"
            className="flex items-center justify-center h-48 text-neutral-500 text-sm"
          >
            {t('channelRules.selectChannel')}
          </div>
        )}
      </div>
    </div>
  )
}
